import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/AppShell";
import EmptyState from "../../components/EmptyState";
import { api, API, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Boxes, Download, Trash2, Upload, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function DocumentsAdmin() {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => setDocs((await api.get("/documents")).data);
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("classification", "General");
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (d) => {
    try {
      const r = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (d) => {
    if (!window.confirm(`Delete "${d.filename}"?`)) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell
      title="Enterprise Document Vault"
      subtitle={`${docs.length} document(s) · SHA-256 versioned · object-storage backed`}
      actions={
        <>
          <input ref={fileRef} type="file" onChange={upload} className="hidden" data-testid="documents-file-input" />
          <Link to="/admin/documents" data-testid="documents-goto-doc-center">
            <Button variant="outline" className="mr-2" data-testid="documents-doc-center-btn">
              <ExternalLink className="w-4 h-4 mr-1" /> Central Document Center
            </Button>
          </Link>
          <Button onClick={() => fileRef.current?.click()} disabled={busy} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="documents-upload-btn">
            <Upload className="w-4 h-4 mr-1" /> {busy ? "Uploading…" : "Upload"}
          </Button>
        </>
      }
    >
      {docs.length === 0 ? (
        <EmptyState icon={Boxes} title="No documents yet" subtitle="Upload contracts, insurance, permits, and specs. Files are content-hashed and versioned automatically." />
      ) : (
        <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
          <table className="w-full erf-table">
            <thead className="bg-erf-creamSecondary">
              <tr>
                <th className="text-left px-3 py-2">Filename</th>
                <th className="text-left px-3 py-2">Class</th>
                <th className="text-left px-3 py-2 mono">v</th>
                <th className="text-left px-3 py-2 mono">size</th>
                <th className="text-left px-3 py-2 mono">sha256</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} className="border-t border-erf-borderCream/60" data-testid={`doc-row-${d.id}`}>
                  <td className="px-3 py-2 font-nav">{d.filename}</td>
                  <td className="px-3 py-2 mono text-[11px]">{d.classification}</td>
                  <td className="px-3 py-2 mono">{d.version}</td>
                  <td className="px-3 py-2 mono text-[11px]">{d.size}</td>
                  <td className="px-3 py-2 mono text-[10px] text-erf-dark/60">{(d.sha256||"").slice(0,12)}…</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => download(d)} data-testid={`doc-download-${d.id}`}><Download className="w-4 h-4"/></Button>
                    <Button size="sm" variant="ghost" className="text-red-700" onClick={() => del(d)} data-testid={`doc-delete-${d.id}`}><Trash2 className="w-4 h-4"/></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
